/** Angular */
import { Injectable } from '@angular/core';
import { AngularFireMessaging } from '@angular/fire/messaging';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';

/** Models */
import { Settings } from './app.settings.model';

/** Externals */
import { HandleError, HttpErrorHandler } from './http-error-handler.service';
import { AppSettings } from './app.settings';
import { environment } from '../environments/environment';
import { Util } from './shared/utils/util';
import { ToastrService } from 'ngx-toastr';

/** Services */
import { EndpointLocationProviderService } from './shared/services/endpoint-location-provider.service';
import { AuthenticationService } from './core/authentication/authentication.service';

@Injectable()
export class FirebaseCloudMessageService {
    private env = environment;
    private handleError: HandleError;
    public settings: Settings;

    public unreadedNotificationsList: BehaviorSubject<any[] | Object> = new BehaviorSubject([]);

    public alreadySubscribed = false;

    constructor(
        private http: HttpClient,
        public appSettings: AppSettings,
        private authService: AuthenticationService,
        httpErrorHandler: HttpErrorHandler,
        private endpointLocationService: EndpointLocationProviderService,
        private angularFireMessaging: AngularFireMessaging,
        private toastr: ToastrService
    ) {
        this.handleError = httpErrorHandler.createHandleError('FirebaseCloudMessageService');
        this.settings = this.appSettings.settings;
    }

    /**
     * Solicita a permissão ao usuário para enviar notificações e cria o token único para ser notificado.
     */
    public requestPermission(): void {
        // Se a permissão do usuário não estiver como negada, inicia a solicitação de permissão
        if (Notification.permission !== 'denied') {
            this.angularFireMessaging.requestToken
                .subscribe(
                    (token) => {

                        // Caso o token permissionado esteja nas configurações do usuário.
                        if (this.settings.FCMTokens.some(tokens => tokens === token)) {

                            // Inscreve o usuário para receber as push notifications.
                            this.receiveMessages();
                        } else { // Caso o token permissionado não esteja nas configurações.

                            // Verifica se o token gerado/existente está vinculado a algum usuário
                            this.FCMTokenExists({ FCMTokens: [token] }).subscribe(exists => {

                                if (exists === '') { // Caso não exista

                                    // Inscreve o token no tópico do GerenciamentoAC.
                                    this.subscribeToTopic(token, `GerenciamentoAC-${Util.getACByHost()}-${Util.getEnvironmentByHost()}`);
                                    this.settings.FCMTokens.push(token);
                                    this.settings.FCMTopics.push(`GerenciamentoAC-${Util.getACByHost()}-${Util.getEnvironmentByHost()}`);

                                    const config = JSON.stringify(this.settings.configs);
                                    const theme = JSON.stringify(this.settings.theme);
                                    const preferences = {
                                        ConfigPreference: config,
                                        ThemePreference: theme,
                                        FCMTokens: [token],
                                        FCMTopics: [`GerenciamentoAC-${Util.getACByHost()}-${Util.getEnvironmentByHost()}`]
                                    };

                                    // Salva as configurações de token e tópico do usuário.
                                    this.manageFCMConfiguration(preferences);

                                    // Se inscreve para receber as notificações.
                                    this.receiveMessages();

                                } else { // Caso exista

                                    // Remove o token que não irá mais existir para nenhum usuário.
                                    this.removeFCMConfiguration({ Id: exists, FCMTokens: [token] });

                                    // Flush no token atual da sessão.
                                    this.removePermission().subscribe(
                                        (token) => {
                                            if (token) {
                                                console.log('token removido!', token);
                                                this.requestPermission();
                                            }
                                        },
                                        (error) => {
                                            console.log('erro!', error);
                                        }
                                    );
                                }
                            });
                        }
                    },
                    (error) => {
                        console.log('erro!', error);
                    }
                );
        }
    }

    /**
     * Remove o token do usuário caso ele não queira mais receber nenhuma notificação.
     */
    public removePermission(): Observable<any> {
        return this.angularFireMessaging.getToken
            .pipe(mergeMap(token => this.angularFireMessaging.deleteToken(token)));
    }

    /**
     * Habilita o recebimento das mensagens após a inscrição.
     */
    public receiveMessages() {
        // Informa o singleton que está inscrito
        this.alreadySubscribed = true;

        this.getUnreadedNotificationsList();

        this.angularFireMessaging.messages.subscribe(
            (payload) => {
                this.getUnreadedNotificationsList();
                const message = Object.create(payload);

                this.toastr.info(message.notification.body, message.notification.title, {
                    disableTimeOut: true
                });
            });
    }

    /**
     * subscribe a user for topic
     * @param token FCM token
     * @param topic nome do tópico
     */
    private subscribeToTopic(token: string, topic: string) {
        return this.http
            .get(`${this.endpointLocationService.getEndpointURI(this.env.API_ENDPOINT_FIREBASE)}/api/SubscribeToTopic/${token}/${topic}`)
            .pipe(
                catchError(this.handleError('Inscrever Usuário em um tópico', []))
            ).subscribe(x => x);
    }

    /**
     * Atualiza as preferências do usuário junto com os tokens e tópicos.
     * @param userPreference Preferências do usuário.
     */
    private manageFCMConfiguration(userPreference) {
        return this.http
            .post(`${this.endpointLocationService.getEndpointURI(this.env.API_ENDPOINT_AUTENTICACAO_PREFERENCE)}/ManageFCMConfiguration`, userPreference)
            .pipe(
                catchError(this.handleError('Enviar tokens e tópicos do usuário.', []))
            ).subscribe(x => x);
    }

    /**
     * Remove o token do usuário não autenticado no momento.
     * @param userPreference Preferências do usuário.
     */
    private removeFCMConfiguration(userPreference) {
        return this.http
            .post(`${this.endpointLocationService.getEndpointURI(this.env.API_ENDPOINT_AUTENTICACAO_PREFERENCE)}/RemoveFCMConfiguration`, userPreference)
            .pipe(
                catchError(this.handleError('Remover tokens do usuário.', []))
            ).subscribe(x => x);
    }

    /**
     * Verifica se o token informado existe em algum usuário na base de dados.
     * @param userPreference Preferências do usuário.
     */
    private FCMTokenExists(userPreference) {
        return this.http
            .post(`${this.endpointLocationService.getEndpointURI(this.env.API_ENDPOINT_AUTENTICACAO_PREFERENCE)}/FCMTokenExists`, userPreference)
            .pipe(
                catchError(this.handleError('Verificar se o token existe.', []))
            );
    }

    /**
     * Retorna a lista completa de notificações do usuário.
     */
    public getNotificationsList() {
        return this.authService.getIdentityCPF()
            .pipe(mergeMap(CPF => {
                return this.http
                    .get(`${this.endpointLocationService.getEndpointURI(this.env.API_ENDPOINT_FIREBASE)}/api/GetListByCPF/${CPF}`)
                    .pipe(
                        catchError(this.handleError('Buscar lista de notificações do usuário.', []))
                    );
            }));
    }

    /**
     * Seta a mensagem como já lida.
     * @param Id Id da mensagem que deseja marcar como lida.
     */
    public setMessageToReaded(Id: string) {
        this.http
            .get(`${this.endpointLocationService.getEndpointURI(this.env.API_ENDPOINT_FIREBASE)}/api/SetReadedMessage/${Id}`)
            .pipe(
                catchError(this.handleError('Seta a notificação como lida.', []))
            ).subscribe(x => x);
    }

    /**
     * Remove a mensagem enviada por parâmetro.
     * @param Id Id da mensagem que deseja remover.
     */
    public deleteMessage(Id: string) {
        return this.http
            .get(`${this.endpointLocationService.getEndpointURI(this.env.API_ENDPOINT_FIREBASE)}/api/DeleteMessage/${Id}`)
            .pipe(
                catchError(this.handleError('Remove a notificação do usuário.', []))
            );
    }

    /**
     * Retorna a lista de notificações não lidas do usuário.
     */
    public getUnreadedNotificationsList() {
        this.authService.getIdentityCPF().subscribe(CPF => {
            this.http
                .get(`${this.endpointLocationService.getEndpointURI(this.env.API_ENDPOINT_FIREBASE)}/api/GetUnreadedListByCPF/${CPF}`)
                .pipe(
                    catchError(this.handleError('Buscar lista de notificações não lidas do usuário.', []))
                ).subscribe(notifications => {
                    this.unreadedNotificationsList.next(Util.orderByDate(notifications, 'IncludeDate'));
                });
        });
    }
}
